import React from 'react';
import { Layers, Lock, Rocket, Brain, Cloud, CheckCircle2 } from 'lucide-react';

const reasons = [
  {
    icon: <Layers size={24} />,
    title: 'Scalable Architecture',
    desc: 'Systems designed to grow with your business — from 100 users to 100 million, without rewrites.',
  },
  {
    icon: <Lock size={24} />,
    title: 'Enterprise Security',
    desc: 'Security baked in from day one. Zero-trust, SOC2-aligned practices, encryption everywhere.',
  },
  {
    icon: <Rocket size={24} />,
    title: 'Faster Delivery',
    desc: 'Agile methodology + CI/CD automation = shipping features 3x faster than traditional teams.',
  },
  {
    icon: <Brain size={24} />,
    title: 'AI-First Engineering',
    desc: 'We don\'t bolt on AI — we engineer products with intelligence at their core architecture.',
  },
  {
    icon: <Cloud size={24} />,
    title: 'Cloud Native Systems',
    desc: 'Every product leverages cloud-native patterns: serverless, containers, managed services.',
  },
  {
    icon: <CheckCircle2 size={24} />,
    title: 'Proven Track Record',
    desc: '50+ delivered projects, 99% satisfaction rate, and zero missed critical deadlines.',
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why" style={{ padding: '7rem 0', background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
      <div className="glow-orb" style={{
        width: '700px', height: '700px',
        background: 'radial-gradient(circle, rgba(37,138,203,0.07) 0%, transparent 70%)',
        top: '0', left: '-200px',
      }} />

      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '5rem', alignItems: 'start' }} className="why-grid">
          {/* Left sticky header */}
          <div style={{ position: 'sticky', top: '120px' }}>
            <div className="section-label"><span>◆</span> Why Nuage</div>
            <h2 className="section-heading">
              Why the best teams <span className="gradient-text">choose us</span>
            </h2>
            <p className="section-subtext" style={{ marginBottom: '2rem' }}>
              We combine deep technical expertise with business acumen — delivering not just code, but outcomes.
            </p>
            <a href="#contact" className="btn-primary">
              Work With Us →
            </a>

            {/* Trust bar */}
            <div style={{
              marginTop: '2.5rem', padding: '1.5rem',
              background: 'rgba(37,138,203,0.06)', border: '1px solid var(--border)',
              borderRadius: '12px',
            }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Trusted by teams at
              </div>
              {['Startups', 'Scale-ups', 'Enterprises', 'Agencies'].map(t => (
                <div key={t} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.4rem',
                }}>
                  <span style={{ color: 'var(--accent)', fontSize: '0.6rem' }}>●</span> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Right cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {reasons.map((r, i) => (
              <div key={r.title} className="glass-card" style={{
                display: 'flex', alignItems: 'flex-start', gap: '1.25rem',
                padding: '1.5rem',
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px', flexShrink: 0,
                  background: 'rgba(37,138,203,0.1)', border: '1px solid rgba(37,138,203,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--accent)',
                }}>
                  {r.icon}
                </div>
                <div>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: '0.35rem' }}>
                    {r.title}
                  </h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .why-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
